<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <div class="modal-content">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <div class="header-icon">
            <font-awesome-icon icon="fa-solid fa-shield-halved" v-if="requiresTwoFactor" />
            <font-awesome-icon icon="fa-solid fa-credit-card" v-else />
          </div>
          <div class="header-text">
            <h3 v-if="requiresTwoFactor">Secure Payment Verification</h3>
            <h3 v-else>Process Payment</h3>
            <p v-if="room">Room {{ room.roomNumber }}</p>
          </div>
        </div>
        <button @click="close" class="close-btn" :disabled="loading">
          <font-awesome-icon icon="fa-solid fa-xmark" />
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <div v-if="loading" class="loading-container">
          <div class="spinner-ring"></div>
          <h4>Processing Payment...</h4>
          <p>Please wait while we process your transaction</p>
        </div>

        <div v-else class="payment-container">
          <!-- 2FA Verification Step -->
          <div v-if="requiresTwoFactor && !twoFactorVerified" class="twofactor-section">
            <div class="twofactor-card">
              <div class="twofactor-header">
                <font-awesome-icon icon="fa-solid fa-shield-halved" class="shield-icon" />
                <h4>Two-Factor Authentication Required</h4>
              </div>
              <p class="twofactor-message">
                This booking requires guest verification before payment. An OTP will be sent to the guest.
              </p>

              <div class="verification-step">
                <div v-if="!otpSent" class="otp-initiation">
                  <div class="charge-summary">
                    <h5>Payment Amount</h5>
                    <div class="amount-display">
                      {{ $utils.formatMoney(amountToPay) }}
                    </div>
                  </div>
                  <button
                    @click="initiateTwoFactor"
                    class="btn btn-primary btn-lg"
                    :disabled="loading"
                  >
                    <font-awesome-icon icon="fa-solid fa-paper-plane" />
                    <span v-if="loading">Sending...</span>
                    <span v-else>Send Verification Code</span>
                  </button>
                </div>

                <div v-else class="otp-verification">
                  <div class="otp-info">
                    <font-awesome-icon icon="fa-solid fa-circle-info" />
                    <span>{{ otpMessage || 'Verification code sent to guest' }}</span>
                  </div>

                  <div class="otp-input-group">
                    <label>
                      <font-awesome-icon icon="fa-solid fa-key" />
                      Enter Verification Code
                    </label>
                    <input
                      type="text"
                      v-model="otpCode"
                      class="form-input otp-input"
                      placeholder="Enter 6-digit code"
                      maxlength="6"
                      @input="validateOtpInput"
                    />
                    <small class="input-help">
                      <font-awesome-icon icon="fa-solid fa-clock" />
                      Code expires in {{ otpCountdown }} seconds
                    </small>
                  </div>

                  <div class="otp-actions">
                    <button
                      @click="verifyOtpCode"
                      class="btn btn-success"
                      :disabled="!otpCode || otpCode.length !== 6 || loading"
                    >
                      <font-awesome-icon icon="fa-solid fa-check-circle" />
                      <span v-if="loading">Verifying...</span>
                      <span v-else>Verify & Continue</span>
                    </button>
                    <button
                      @click="resendOtp"
                      class="btn btn-outline-secondary"
                      :disabled="loading || otpCountdown > 0"
                    >
                      <font-awesome-icon icon="fa-solid fa-rotate" />
                      Resend Code
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Summary Card -->
          <div v-if="!requiresTwoFactor || twoFactorVerified" class="summary-section">
            <div class="summary-card">
              <div class="summary-header">
                <font-awesome-icon icon="fa-solid fa-receipt" />
                <h4>Payment Summary</h4>
                <span v-if="requiresTwoFactor && twoFactorVerified" class="verified-badge">
                  <font-awesome-icon icon="fa-solid fa-shield-check" />
                  Verified
                </span>
              </div>

              <div class="summary-info">
                <div class="info-grid">
                  <div class="info-item">
                    <span class="label">
                      <font-awesome-icon icon="fa-solid fa-user" />
                      Guest
                    </span>
                    <span class="value">{{ room?.guestName || 'N/A' }}</span>
                  </div>

                  <div class="info-item">
                    <span class="label">
                      <font-awesome-icon icon="fa-solid fa-money-bill-wave" />
                      Balance Due
                    </span>
                    <span class="value amount">{{ $utils.formatMoney(balanceDue) }}</span>
                  </div>

                  <div class="info-item">
                    <span class="label">
                      <font-awesome-icon icon="fa-solid fa-file-invoice" />
                      Total Bill
                    </span>
                    <span class="value">{{ $utils.formatMoney(room?.totalAmount || 0) }}</span>
                  </div>

                  <div class="info-item">
                    <span class="label">
                      <font-awesome-icon icon="fa-solid fa-wallet" />
                      Method
                    </span>
                    <span class="value">{{ room?.preferredPaymentMethod || 'Cash' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Method Selection -->
          <div v-if="!requiresTwoFactor || twoFactorVerified" class="methods-section">
            <h4 class="section-title">
              <font-awesome-icon icon="fa-solid fa-wallet" />
              Select Payment Method
            </h4>

            <div v-if="loading" class="loading-methods">
              <div class="spinner-small"></div>
              <p>Loading payment methods...</p>
            </div>

            <div v-else-if="availablePaymentMethods.length === 0" class="no-methods">
              <p>No payment methods available</p>
            </div>

            <div v-else class="method-grid">
              <button
                v-for="method in availablePaymentMethods"
                :key="method"
                class="method-card"
                :class="{ active: selectedMethod === method }"
                @click="selectPaymentMethod(method)"
              >
                <span>{{ method }}</span>
              </button>
            </div>
          </div>

          <!-- Payment Details Form -->
          <div v-if="(!requiresTwoFactor || twoFactorVerified) && selectedMethod" class="details-section">
            <h4 class="section-title">
              <font-awesome-icon icon="fa-solid fa-pen-to-square" />
              Payment Details
            </h4>

            <div class="info-banner">
              <font-awesome-icon icon="fa-solid fa-lightbulb" />
              <span>You can enter any amount - partial, full, or advance payment</span>
            </div>

            <div class="form-container">
              <!-- Payment Amount (All Methods) -->
              <div class="form-section">
                <div class="input-group">
                  <label for="amountToPay">
                    <font-awesome-icon icon="fa-solid fa-dollar-sign" />
                    Amount
                  </label>
                  <div class="input-wrapper">
                    <input
                      id="amountToPay"
                      type="number"
                      class="form-input"
                      v-model.number="amountToPay"
                      placeholder="Enter amount"
                      step="0.01"
                      min="0.01"
                    />
                    <span class="input-icon currency">
                      ZMW
                    </span>
                  </div>
                  <div class="amount-actions" v-if="balanceDue > 0">
                    <button type="button" @click="amountToPay = balanceDue" class="btn-quick-fill">
                      <font-awesome-icon icon="fa-solid fa-wand-magic-sparkles" />
                      Fill Balance ({{ $utils.formatMoney(balanceDue) }})
                    </button>
                  </div>
                  <small class="input-help" v-if="room?.totalAmount">
                    <font-awesome-icon icon="fa-solid fa-circle-info" />
                    Total due: {{ $utils.formatMoney(balanceDue) }} | Total bill: {{ $utils.formatMoney(room.totalAmount) }}
                  </small>
                </div>
              </div>

              <!-- Kazang Fields -->
              <div v-if="isKazangPayment(selectedMethod)" class="form-section">
                <div class="input-group">
                  <label for="customerPhone">
                    <font-awesome-icon icon="fa-solid fa-phone" />
                    Customer Phone Number
                  </label>
                  <div class="input-wrapper">
                    <input
                      id="customerPhone"
                      type="tel"
                      class="form-input"
                      v-model="customerPhone"
                      placeholder="Enter phone number (e.g., 096...)"
                      maxlength="10"
                    />
                    <span class="input-icon">
                      <font-awesome-icon icon="fa-solid fa-mobile-screen" />
                    </span>
                  </div>
                  <small class="input-help" v-if="!isPhoneValid && customerPhone.length > 0">
                    <font-awesome-icon icon="fa-solid fa-circle-exclamation" />
                    Please enter a valid 10-digit phone number
                  </small>
                </div>
              </div>

              <!-- Payment Notes -->
              <div class="input-group">
                <label for="paymentNotes">
                  <font-awesome-icon icon="fa-solid fa-note-sticky" />
                  Notes (Optional)
                </label>
                <textarea
                  id="paymentNotes"
                  class="form-textarea"
                  v-model="paymentNotes"
                  rows="3"
                  placeholder="Add any payment notes..."
                ></textarea>
              </div>

              <!-- Payment Validation Summary -->
              <div class="validation-summary" v-if="selectedMethod">
                <div class="validation-item" :class="{ valid: canProcessPayment, invalid: !canProcessPayment }">
                  <font-awesome-icon :icon="canProcessPayment ? 'fa-solid fa-circle-check' : 'fa-solid fa-circle-xmark'" />
                  <span>{{ canProcessPayment ? 'Ready to process payment' : 'Please complete required fields' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button @click="close" class="btn btn-secondary" :disabled="loading">
          <font-awesome-icon icon="fa-solid fa-xmark" />
          Cancel
        </button>
        <button
          v-if="!requiresTwoFactor || twoFactorVerified"
          @click="processPayment"
          class="btn btn-primary"
          :disabled="!canProcessPayment || loading"
        >
          <font-awesome-icon icon="fa-solid fa-check" />
          <span v-if="loading">Processing...</span>
          <span v-else>Process Payment</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import LodgingIntegration from '@/services/lodgingIntegration';
import axios from 'axios';
import { Storage } from '@/services/Storage';
import { errorHandler } from '@/services/errorHandler';

export default {
  name: 'PaymentModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    room: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedMethod: null,
      amountToPay: 0,
      paymentNotes: '',
      customerPhone: '',
      loading: false,
      availablePaymentMethods: [],
      requiresTwoFactor: false,
      twoFactorVerified: false,
      otpSent: false,
      otpCode: '',
      otpCountdown: 0,
      otpTimer: null,
      transactionId: null,
      otpMessage: ''
    };
  },
  computed: {
    kazangSessionID() {
      return this.$store.getters.getKazangSessionID;
    },

    balanceDue() {
      if (!this.room) return 0;
      return this.room.balanceDue ||
            (this.room.totalAmount && this.room.totalPaid
              ? (this.room.totalAmount - this.room.totalPaid)
              : this.room.totalAmount) || 0;
    },

    canProcessPayment() {
      if (!this.selectedMethod) return false;

      // Check if we have a valid room with booking/financial information
      if (!this.room) {
        return false;
      }

      // Check if amount is valid (required for all payment methods)
      if (!this.amountToPay || this.amountToPay <= 0) {
        return false;
      }

      // For Kazang, also check phone number
      if (this.isKazangPayment(this.selectedMethod)) {
        return this.customerPhone && this.customerPhone.length >= 10;
      }

      // For Cash, Card, and Account, only amount is required
      return true;
    },

    isPhoneValid() {
      if (!this.customerPhone) return false;
      return this.customerPhone.length === 10 && /^\d+$/.test(this.customerPhone);
    }
  },
  watch: {
    show: {
      immediate: true,
      async handler(newVal) {
        if (newVal && this.room) {
          this.resetForm();

          // Pre-fill amount with balance due
          this.amountToPay = this.balanceDue;

          // Check if 2FA is required for this booking
          this.requiresTwoFactor = this.room.serviceTwoFactorEnabled === true;

          await this.fetchPaymentMethods();
        }
      }
    }
  },
  methods: {
    async fetchPaymentMethods() {
      try {

        this.loading = true;
        let storeDB = await Storage.getCurrentStoreDB();

        const response = await fetch(`https://lytningtomcat.chrilantech.com/Lyt-SoftBackOfficeApi/chartOfAccounts/${storeDB}/searchSafes?safeName`);

        if (!response.ok) {
          throw new Error('Failed to fetch payment methods');
        }

        const data = await response.json();

        // Extract payment methods from the response
        // Assuming the API returns an array of methods
        this.availablePaymentMethods = Array.isArray(data) ? data : [];

        console.log('Fetched payment methods:', this.availablePaymentMethods);

      } catch (error) {
        console.error('Error fetching payment methods:', error);

        // Fallback to default methods
        this.availablePaymentMethods = ['Cash', 'Card', 'Kazang', 'Account'];

        Swal.fire({
          title: 'Warning',
          text: 'Could not load payment methods from server. Using defaults.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
      } finally {
        this.loading = false;
      }
    },

    selectPaymentMethod(methodName) {
      this.selectedMethod = methodName;
    },

    async processPayment() {
      if (!this.canProcessPayment) {
        Swal.fire({
          title: 'Validation Error',
          text: 'Please complete all required fields before processing payment',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      // Check if 2FA is required and not yet verified
      if (this.requiresTwoFactor && !this.twoFactorVerified) {
        Swal.fire({
          title: 'Verification Required',
          text: 'This booking requires guest verification before payment. Please complete the two-factor authentication process first.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      let user = await Storage.getCurrentUser();

      this.loading = true;

      try {
        // Use the amountToPay that the user entered
        const amountToPay = Number.parseFloat(this.amountToPay.toString());

        console.log('Processing payment with amount:', amountToPay);

        // Validate the amount is positive
        if (isNaN(amountToPay) || amountToPay <= 0) {
          throw new Error('Please enter a valid payment amount');
        }

        // Note: Users can now pay any amount, including more than the balance
        // This allows for advance payments or custom amounts as needed
        const balanceDue = this.balanceDue;

        if (amountToPay > balanceDue && balanceDue > 0) {
          const result = await Swal.fire({
            title: 'Payment Exceeds Balance',
            text: `This payment (${this.$utils.formatMoney(amountToPay)}) will create a credit of ${this.$utils.formatMoney(amountToPay - balanceDue)}. Continue?`,
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Yes, proceed',
            cancelButtonText: 'No, adjust amount',
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d'
          });

          if (!result.isConfirmed) {
            this.loading = false;
            return;
          }
        }

        let transactionReference = `PAY-${Date.now()}`;

        const paymentData = {
          amount: amountToPay,
          user: user.Username,
          safeName: this.selectedMethod.toUpperCase(),
          paymentMethod: "CASH",
          transactionReference: transactionReference,
          notes: this.paymentNotes || '',
          // Include customer phone for Kazang payments
          customerPhone: this.isKazangPayment(this.selectedMethod) ? this.customerPhone : null,
          // Include OTP transaction ID if 2FA was used
          bookingOtpTransactionId: this.transactionId
        };

        console.log('Payment data being sent:', paymentData);

        // Use bookingId from room object
        const bookingId = this.room.bookingId || this.room.id;

        if (!bookingId) {
          throw new Error('Booking ID is required to process payment');
        }

        // Call the lodging integration service
        await LodgingIntegration.processPayment(bookingId, paymentData);

        console.log('Payment processed successfully for booking:', bookingId, 'Amount:', amountToPay);

        this.$emit('payment-complete', paymentData);
        this.close();

        // Show success message
        Swal.fire({
          title: 'Payment Successful',
          text: `Payment of ${this.$utils.formatMoney(amountToPay)} has been processed successfully`,
          icon: 'success',
          timer: 3000,
          showConfirmButton: false
        });

      } catch (error) {
        console.error('Payment processing failed:', error);
        Swal.fire({
          title: 'Payment Failed',
          text: error.message || 'Failed to process payment. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.loading = false;
      }
    },

   

    getIconForMethod(methodName) {
      const normalizedName = methodName.toLowerCase();
      const iconMap = {
        'cash': 'fa-solid fa-money-bills',
        'card': 'fa-solid fa-credit-card',
        'account': 'fa-solid fa-address-card',
        'kazang': '@/assets/img/Kazang.png',
        'accountsale': 'fa-solid fa-address-card'
      };
      return iconMap[normalizedName] || 'fa-solid fa-circle';
    },

    isKazangPayment(methodName) {
      const normalizedName = methodName.toLowerCase();
      return normalizedName === 'kazang';
    },

    async initiateTwoFactor() {
      this.loading = true;
      try {
        const bookingId = this.room.bookingId || this.room.id;

        if (!bookingId) {
          throw new Error('Booking ID is required');
        }

        // Initiate 2FA with charge details
        const chargeData = {
          description: 'Room Payment',
          billTotal: this.amountToPay,
          tipAmount: 0,
          itemType: 'LODGING'
        };

        console.log('=== PaymentModal 2FA Amount Debug ===');
        console.log('Amount to Pay:', this.amountToPay);
        console.log('Room Details:', this.room);
        console.log('Charge Data:', chargeData);
        console.log('=====================================');

        const response = await LodgingIntegration.initiateSecureCharge(bookingId, chargeData);

        this.transactionId = response.transactionId;
        this.otpSent = true;
        this.otpMessage = response.message || 'Verification code sent to guest via Email and WhatsApp';

        // Start countdown
        this.startOtpCountdown();

        console.log('2FA initiated, transaction ID:', this.transactionId);

      } catch (error) {
        console.error('Failed to initiate 2FA:', error);
        Swal.fire({
          title: 'Verification Failed',
          text: error.message || 'Failed to send verification code',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.loading = false;
      }
    },

    async verifyOtpCode() {
      if (!this.otpCode || this.otpCode.length !== 6) {
        return;
      }

      this.loading = true;
      try {
        const response = await LodgingIntegration.verifySecureCharge(this.transactionId, this.otpCode);

        if (response.status === 'verified') {
          this.twoFactorVerified = true;
          this.otpSent = false;

          if (this.otpTimer) {
            clearInterval(this.otpTimer);
            this.otpTimer = null;
          }

          Swal.fire({
            title: 'Verification Successful',
            text: 'Code verified. You can now proceed with the payment.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
        }

      } catch (error) {
        console.error('OTP verification failed:', error);

        let errorMessage = 'Invalid verification code';
        if (error.response?.status === 410) {
          errorMessage = 'Verification code has expired';
        } else if (error.response?.status === 400) {
          errorMessage = 'Invalid transaction or verification attempt';
        }

        Swal.fire({
          title: 'Verification Failed',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });

        // Clear the code so user can try again
        this.otpCode = '';
      } finally {
        this.loading = false;
      }
    },

    async resendOtp() {
      await this.initiateTwoFactor();
      this.otpCode = '';
    },

    validateOtpInput() {
      // Only allow digits
      this.otpCode = this.otpCode.replace(/\D/g, '');
    },

    startOtpCountdown() {
      this.otpCountdown = 120; // 2 minutes

      if (this.otpTimer) {
        clearInterval(this.otpTimer);
      }

      this.otpTimer = setInterval(() => {
        this.otpCountdown--;

        if (this.otpCountdown <= 0) {
          clearInterval(this.otpTimer);
          this.otpTimer = null;
        }
      }, 1000);
    },

    resetForm() {
      this.selectedMethod = null;
      this.amountToPay = 0;
      this.paymentNotes = '';
      this.customerPhone = '';
      this.availablePaymentMethods = [];
      this.requiresTwoFactor = false;
      this.twoFactorVerified = false;
      this.otpSent = false;
      this.otpCode = '';
      this.otpCountdown = 0;
      this.transactionId = null;
      this.otpMessage = '';
      if (this.otpTimer) {
        clearInterval(this.otpTimer);
        this.otpTimer = null;
      }
    },

    close() {
      this.resetForm();
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Modal Overlay & Container */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  animation: fadeIn 0.2s ease-out;
}

.modal-content {
  background: var(--card-bg);
  border-radius: 20px;
  max-width: 700px;
  width: 92%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.3),
    0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--card-border);
  animation: slideUp 0.3s ease-out;
  display: flex;
  flex-direction: column;
}

/* Header */
.modal-header {
  padding: 24px 28px;
  background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.header-text h3 {
  margin: 0 0 4px 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.header-text p {
  margin: 0;
  opacity: 0.95;
  font-size: 0.95rem;
}

.close-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: white;
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.25);
  transform: rotate(90deg);
}

/* Body */
.modal-body {
  padding: 28px;
  overflow-y: auto;
  flex: 1;
  background: var(--bg-primary);
}

/* Loading State */
.loading-container {
  text-align: center;
  padding: 80px 40px;
}

.spinner-ring {
  width: 64px;
  height: 64px;
  margin: 0 auto 24px;
  border: 4px solid rgba(var(--accent-electric-blue-rgb), 0.2);
  border-top-color: var(--accent-electric-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-container h4 {
  margin: 0 0 8px 0;
  color: var(--text-primary);
  font-size: 1.4rem;
  font-weight: 600;
}

.loading-container p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 1rem;
}

/* Payment Container */
.payment-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Summary Section */
.summary-section {
  animation: fadeInUp 0.4s ease-out;
}

.summary-card {
  background: linear-gradient(135deg, rgba(var(--success-rgb), 0.08), rgba(var(--success-rgb), 0.04));
  border: 2px solid var(--success);
  border-radius: 16px;
  padding: 20px;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  color: var(--success);
  position: relative;
}

.summary-header h4 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
}

.summary-header svg {
  font-size: 1.4rem;
}

.verified-badge {
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: var(--success);
  color: white;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(var(--success-rgb), 0.3);
}

.verified-badge svg {
  font-size: 0.9rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  background: rgba(var(--success-rgb), 0.05);
  border-radius: 10px;
}

.label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 0.85rem;
  font-weight: 600;
}

.label svg {
  font-size: 0.9rem;
}

.value {
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  word-break: break-word;
}

.value.amount {
  color: var(--success);
  font-size: 1.5rem;
  font-weight: 700;
}

/* Methods Section */
.methods-section {
  animation: fadeInUp 0.5s ease-out;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 16px 0;
  color: var(--text-primary);
  font-size: 1.2rem;
  font-weight: 700;
}

.section-title svg {
  color: var(--accent-electric-blue);
  font-size: 1.1rem;
}

.method-card {
  width: 100%;
  background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
  border: 2px solid var(--accent-electric-blue);
  border-radius: 10px;
  padding: 16px 12px;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  box-shadow: var(--card-shadow);
}

.method-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--card-shadow-hover);
}

.method-card.active {
  background: linear-gradient(135deg, var(--accent-electric-blue-dark), var(--accent-neon-purple-dark));
  border-color: var(--accent-electric-blue);
  box-shadow: 0 8px 24px rgba(var(--accent-electric-blue-rgb), 0.3);
  transform: translateY(-2px);
}

.method-card span {
  text-align: center;
  line-height: 1.2;
}

.loading-methods,
.no-methods {
  text-align: center;
  padding: 40px 20px;
  color: var(--text-secondary);
}

.spinner-small {
  width: 32px;
  height: 32px;
  margin: 0 auto 16px;
  border: 3px solid rgba(var(--accent-electric-blue-rgb), 0.2);
  border-top-color: var(--accent-electric-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.method-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

/* Details Section */
.details-section {
  animation: fadeInUp 0.6s ease-out;
}

.info-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(135deg, rgba(var(--accent-electric-blue-rgb), 0.1), rgba(var(--accent-neon-purple-rgb), 0.1));
  border: 1px solid var(--accent-electric-blue);
  border-radius: 10px;
  margin-bottom: 16px;
  color: var(--accent-electric-blue);
  font-weight: 600;
  font-size: 0.95rem;
}

.info-banner svg {
  font-size: 1.2rem;
}

.form-container {
  background: var(--bg-secondary);
  border: 1px solid var(--card-border);
  border-radius: 14px;
  padding: 24px;
}

.form-section {
  margin-bottom: 24px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-group label {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 4px;
}

.input-group label svg {
  color: var(--accent-electric-blue);
  font-size: 0.95rem;
}

.input-wrapper {
  position: relative;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 14px 16px;
  background: var(--card-bg);
  border: 2px solid var(--card-border);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 1rem;
  transition: all 0.25s ease;
  font-family: inherit;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--accent-electric-blue);
  box-shadow: 0 0 0 4px rgba(var(--accent-electric-blue-rgb), 0.1);
  background: var(--bg-primary);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.input-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 600;
}

.input-icon.currency {
  color: var(--success);
}

.input-help {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #e74c3c;
  font-size: 0.85rem;
  margin-top: 4px;
}

.amount-actions {
  margin-top: 8px;
}

.btn-quick-fill {
  background: linear-gradient(135deg, var(--success), #20c997);
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.25s ease;
  box-shadow: 0 2px 8px rgba(var(--success-rgb), 0.2);
}

.btn-quick-fill:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(var(--success-rgb), 0.3);
}

.btn-quick-fill svg {
  font-size: 0.9rem;
}

/* Change Card */
.change-card {
  margin-top: 16px;
  background: var(--card-bg);
  border: 2px solid var(--success);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.change-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.change-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 600;
}

.change-label svg {
  color: var(--success);
}

.change-amount {
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--success);
}

.change-indicator {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--success);
  background: rgba(var(--success-rgb), 0.1);
}

.change-indicator.insufficient {
  color: #e74c3c;
  background: rgba(231, 76, 60, 0.1);
}

/* Validation Summary */
.validation-summary {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--card-border);
}

.validation-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
}

.validation-item.valid {
  background: rgba(var(--success-rgb), 0.1);
  color: var(--success);
}

.validation-item.invalid {
  background: rgba(231, 76, 60, 0.1);
  color: #e74c3c;
}

/* Footer */
.modal-footer {
  padding: 24px 28px;
  background: var(--bg-secondary);
  border-top: 1px solid var(--card-border);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn {
  padding: 14px 28px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.25s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: none;
  cursor: pointer;
}

.btn-primary {
  background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
  box-shadow: 0 4px 12px rgba(var(--accent-electric-blue-rgb), 0.2);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(var(--accent-electric-blue-rgb), 0.35);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: var(--secondary-button-bg);
  color: var(--secondary-button-text);
  border: 2px solid var(--card-border);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--secondary-button-bg-dark);
  transform: translateY(-2px);
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 640px) {
  .modal-content {
    width: 95%;
    max-height: 95vh;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 20px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .method-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .change-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .modal-footer {
    flex-direction: column-reverse;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}

/* Two-Factor Authentication Styles */
.twofactor-section {
  animation: fadeInUp 0.4s ease-out;
}

.twofactor-card {
  background: linear-gradient(135deg, rgba(var(--accent-electric-blue-rgb), 0.08), rgba(var(--accent-neon-purple-rgb), 0.08));
  border: 2px solid var(--accent-electric-blue);
  border-radius: 16px;
  padding: 32px;
  text-align: center;
}

.twofactor-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.twofactor-header h4 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 700;
}

.shield-icon {
  font-size: 3rem;
  color: var(--accent-electric-blue);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

.twofactor-message {
  color: var(--text-secondary);
  font-size: 1rem;
  margin-bottom: 24px;
  line-height: 1.6;
}

.verification-step {
  margin-top: 24px;
}

.otp-initiation {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
}

.charge-summary {
  background: var(--card-bg);
  border: 2px solid var(--success);
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 300px;
}

.charge-summary h5 {
  margin: 0 0 12px 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 600;
}

.amount-display {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--success);
}

.btn-lg {
  padding: 18px 36px;
  font-size: 1.1rem;
  width: 100%;
  max-width: 350px;
}

.otp-verification {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.otp-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  background: rgba(var(--accent-electric-blue-rgb), 0.1);
  border: 1px solid var(--accent-electric-blue);
  border-radius: 10px;
  color: var(--accent-electric-blue);
  font-weight: 600;
}

.otp-info svg {
  font-size: 1.2rem;
}

.otp-input-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  margin: 0 auto;
}

.otp-input-group label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.1rem;
}

.otp-input {
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 8px;
  font-weight: 700;
  padding: 16px;
  background: var(--card-bg);
  border: 2px solid var(--accent-electric-blue);
  border-radius: 12px;
  color: var(--text-primary);
}

.otp-input:focus {
  border-color: var(--accent-electric-blue);
  box-shadow: 0 0 0 4px rgba(var(--accent-electric-blue-rgb), 0.15);
}

.otp-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.otp-actions .btn {
  min-width: 180px;
}

.btn-success {
  background: linear-gradient(135deg, var(--success), #20c997);
  color: white;
  box-shadow: 0 4px 12px rgba(var(--success-rgb), 0.2);
}

.btn-success:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(var(--success-rgb), 0.35);
}

.btn-outline-secondary {
  background: transparent;
  border: 2px solid var(--text-secondary);
  color: var(--text-secondary);
}

.btn-outline-secondary:hover:not(:disabled) {
  background: var(--text-secondary);
  color: var(--card-bg);
  transform: translateY(-2px);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}
</style>
